import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const usePushNotifications = (token) => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [permission, setPermission] = useState('default');
  const [error, setError] = useState(null);

  // Check if push notifications are supported
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported && 'Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  // Register service worker
  const registerServiceWorker = useCallback(async () => {
    if (!isSupported) return null;
    
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration);
      return registration;
    } catch (err) {
      console.error('Service Worker registration failed:', err);
      setError('Kunne ikke registrere service worker');
      return null;
    }
  }, [isSupported]);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!isSupported) return false;
    
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === 'granted';
    } catch (err) {
      console.error('Permission request failed:', err);
      setError('Kunne ikke anmode om tilladelse');
      return false;
    }
  }, [isSupported]);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported || !token) return false;
    
    try {
      // Request permission first
      const hasPermission = await requestPermission();
      if (!hasPermission) {
        setError('Notifikationer er ikke tilladt');
        return false;
      }
      
      // Register service worker
      const registration = await registerServiceWorker();
      if (!registration) return false;
      
      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;
      
      // Get existing subscription or create new one
      let pushSubscription = await registration.pushManager.getSubscription();
      
      if (!pushSubscription) {
        // Generate VAPID key (in production, get from server)
        // For now, we'll use a placeholder - the backend will need to be configured
        const vapidKey = await getVapidPublicKey();
        
        if (!vapidKey) {
          // Fallback: Create subscription without applicationServerKey
          // This works for some browsers but not all
          console.log('No VAPID key available, using basic subscription');
        }
        
        const options = {
          userVisibleOnly: true,
        };
        
        if (vapidKey) {
          options.applicationServerKey = urlBase64ToUint8Array(vapidKey);
        }
        
        pushSubscription = await registration.pushManager.subscribe(options);
      }
      
      // Send subscription to backend
      const subscriptionJSON = pushSubscription.toJSON();
      
      await axios.post(`${API}/api/notifications/subscribe`, {
        endpoint: subscriptionJSON.endpoint,
        keys: subscriptionJSON.keys
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSubscription(pushSubscription);
      setIsSubscribed(true);
      setError(null);
      
      console.log('Push notification subscription successful');
      return true;
    } catch (err) {
      console.error('Subscribe error:', err);
      setError('Kunne ikke aktivere push notifikationer');
      return false;
    }
  }, [isSupported, token, requestPermission, registerServiceWorker]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!subscription || !token) return false;
    
    try {
      await subscription.unsubscribe();
      
      await axios.delete(`${API}/api/notifications/unsubscribe`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSubscription(null);
      setIsSubscribed(false);
      
      console.log('Push notification unsubscription successful');
      return true;
    } catch (err) {
      console.error('Unsubscribe error:', err);
      setError('Kunne ikke deaktivere push notifikationer');
      return false;
    }
  }, [subscription, token]);

  // Get VAPID public key from server
  const getVapidPublicKey = async () => {
    try {
      const response = await axios.get(`${API}/api/notifications/push/vapid-key`);
      return response.data.publicKey;
    } catch (err) {
      console.log('VAPID key not available');
      return null;
    }
  };

  // Check existing subscription on mount
  useEffect(() => {
    if (!isSupported) return;
    
    const checkSubscription = async () => {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          const existingSubscription = await registration.pushManager.getSubscription();
          if (existingSubscription) {
            setSubscription(existingSubscription);
            setIsSubscribed(true);
          }
        }
      } catch (err) {
        console.error('Error checking subscription:', err);
      }
    };
    
    checkSubscription();
  }, [isSupported]);

  return {
    isSupported,
    isSubscribed,
    permission,
    error,
    subscribe,
    unsubscribe,
    requestPermission
  };
};

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  
  return outputArray;
}

export default usePushNotifications;
